import { Link } from "react-router-dom";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Info, ThumbsUp } from "lucide-react";
import { Card, Kpi } from "../stats/StatsParts";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
const LEVEL = { insuffisante: "text-rose-700 bg-rose-50", faible: "text-amber-800 bg-amber-50", moyenne: "text-sky-800 bg-sky-50", bonne: "text-emerald-800 bg-emerald-50" };
const Empty = ({ children }) => <p className="text-sm text-slate-500">{children}</p>;

export const StatusBanner = ({ status }) => (status && !status.available ? (
  <div className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="ai-unavailable-banner">
    <Info className="mt-0.5 h-4 w-4 shrink-0" /><div><p className="font-semibold">Analyse par IA indisponible</p><p>{status.reason}</p></div>
  </div>
) : null);

export const Summary = ({ o }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi testId="ins-kpi-analyzed" label="Feedbacks analysés" value={o.analyzed} hint={`${o.previous_analyzed} sur la période précédente`} />
      <Kpi testId="ins-kpi-avg" label="Note moyenne" value={o.avg_rating ?? "—"} hint={`précédente : ${o.previous_avg_rating ?? "—"}`} />
      <Kpi testId="ins-kpi-ai" label="Classés par l'IA" value={o.ai_classified} hint={`${o.pending_classification} en attente`} />
      <Kpi testId="ins-kpi-signals" label="Signaux d'attention" value={o.signals.length} />
    </div>
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="ins-period">
      <span className="rounded-full bg-slate-100 px-3 py-1">Période : {fmt(o.period.from)} – {fmt(o.period.to)}</span>
      <span className="rounded-full bg-slate-100 px-3 py-1">Comparée au {fmt(o.previous_period.from)} – {fmt(o.previous_period.to)}</span>
      <span className={`rounded-full px-3 py-1 font-medium ${LEVEL[o.confidence.level]}`} data-testid="ins-confidence">Fiabilité {o.confidence.level} · {o.confidence.text}</span>
    </div>
    {o.limits.length > 0 && <ul className="space-y-0.5 text-xs text-slate-500" data-testid="ins-limits">{o.limits.map((l) => <li key={l}>• {l}</li>)}</ul>}
  </div>
);

const ThemeTable = ({ rows }) => (!rows.length ? <Empty>Aucun feedback sur la période.</Empty> : (
  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="ins-themes-table">
    <thead><tr className="text-left text-xs text-slate-500"><th className="py-1.5">Thème</th><th>Avis</th><th>Note</th><th>Négatifs</th><th>Évol. nég.</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.theme} className="border-t border-slate-100"><td className="py-1.5 pr-2">{r.theme}</td><td>{r.count}</td><td>{r.avg ?? "—"}</td><td>{r.negative}</td>
        <td className={r.delta_negative > 0 ? "text-rose-700" : r.delta_negative < 0 ? "text-emerald-700" : ""}>{r.delta_negative > 0 ? "+" : ""}{r.delta_negative}</td></tr>
    ))}</tbody>
  </table><p className="mt-2 text-xs text-slate-400">Thème attribué par l'IA ou, à défaut, catégorie choisie par l'usager.</p></div>
));

const Moves = ({ up, down }) => (
  <div className="space-y-3 text-sm">
    {!up.length && !down.length && <Empty>Aucune évolution notable par rapport à la période précédente.</Empty>}
    {up.map((r) => <p key={`u${r.theme}`} className="flex items-center gap-2 text-rose-700" data-testid="ins-rising-item"><ArrowUpRight className="h-4 w-4" />{r.theme} : {r.prev_negative} → {r.negative} avis négatifs</p>)}
    {down.map((r) => <p key={`d${r.theme}`} className="flex items-center gap-2 text-emerald-700" data-testid="ins-declining-item"><ArrowDownRight className="h-4 w-4" />{r.theme} : {r.prev_negative} → {r.negative} avis négatifs</p>)}
  </div>
);

const Signals = ({ items }) => (!items.length ? <Empty>Aucun signal particulier détecté.</Empty> : (
  <ul className="space-y-2 text-sm">{items.map((s, i) => (
    <li key={i} className="flex gap-2" data-testid={`ins-signal-${i}`}><AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${s.level === "haute" ? "text-rose-600" : "text-amber-600"}`} />
      <span><b>{s.label}</b><span className="block text-xs text-slate-500">{s.detail}</span></span></li>
  ))}</ul>
));

const Strengths = ({ items }) => (!items.length ? <Empty>Pas encore assez d'avis pour dégager des points forts (3 avis minimum par élément).</Empty> : (
  <ul className="space-y-2 text-sm">{items.map((s) => <li key={s.label} className="flex gap-2"><ThumbsUp className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /><span>{s.label}<span className="block text-xs text-slate-500">{s.detail}</span></span></li>)}</ul>
));

const Compare = ({ rows, label, testId }) => (!rows.length ? <Empty>Aucune donnée.</Empty> : (
  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid={testId}>
    <thead><tr className="text-left text-xs text-slate-500"><th className="py-1.5">{label}</th><th>Avis</th><th>Note</th><th>Évol.</th><th>% nég.</th></tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r.id} className="border-t border-slate-100"><td className="py-1.5 pr-2">{r.name}</td><td>{r.count}</td><td>{r.avg}</td>
        <td className={r.delta_avg < 0 ? "text-rose-700" : r.delta_avg > 0 ? "text-emerald-700" : ""}>{r.delta_avg === null ? "—" : `${r.delta_avg > 0 ? "+" : ""}${r.delta_avg}`}</td>
        <td className={r.negative_share >= 40 ? "font-semibold text-rose-700" : ""}>{r.negative_share} %</td></tr>
    ))}</tbody>
  </table></div>
));

const Sentiment = ({ s }) => (
  <div className="space-y-2 text-sm" data-testid="ins-sentiment">
    <p><b>Selon la note</b> : {s.by_rating.positive} positifs · {s.by_rating.neutral} neutres · {s.by_rating.negative} négatifs</p>
    <p><b>Selon le texte (IA)</b> : {s.ai_sample ? `${s.by_text_ai.positive} positifs · ${s.by_text_ai.neutral} neutres · ${s.by_text_ai.negative} négatifs sur ${s.ai_sample} avis classés` : "aucun avis classé"}</p>
    <p className="text-xs text-slate-400">{s.rule}</p>
  </div>
);

export const Observations = ({ o, admin = false }) => (
  <div className="space-y-6" data-testid="ins-observations">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Observations calculées sur les feedbacks enregistrés</p>
    <div className="grid gap-6 lg:grid-cols-3">
      <Card title="Thèmes les plus mentionnés" testId="ins-themes" className="lg:col-span-2"><ThemeTable rows={o.themes} /></Card>
      <Card title="Sentiment" testId="ins-sentiment-card"><Sentiment s={o.sentiment} /></Card>
    </div>
    <div className="grid gap-6 lg:grid-cols-3">
      <Card title="Problèmes en hausse / en baisse" testId="ins-moves"><Moves up={o.rising} down={o.declining} /></Card>
      <Card title="Signaux d'attention" testId="ins-signals"><Signals items={o.signals} /></Card>
      <Card title="Points forts relevés" testId="ins-strengths"><Strengths items={o.strengths} /></Card>
    </div>
    <div className="grid gap-6 lg:grid-cols-2">
      {admin && o.by_company.length > 0 && <Card title="Par entreprise" testId="ins-by-company"><Compare rows={o.by_company} label="Entreprise" testId="ins-company-table" /></Card>}
      <Card title="Comparaison des agences" testId="ins-by-agency"><Compare rows={o.by_agency} label="Agence" testId="ins-agency-table" /></Card>
      <Card title="Comparaison des services" testId="ins-by-service"><Compare rows={o.by_service} label="Service" testId="ins-service-table" /></Card>
    </div>
    {o.trend.length > 1 && <Card title="Évolution des notes" testId="ins-trend"><div className="flex flex-wrap gap-3 text-sm">{o.trend.map((t) => <span key={t.month} className="rounded-xl bg-slate-50 px-3 py-2"><b>{t.month}</b> · {t.avg}/5 · {t.count} avis</span>)}</div></Card>}
  </div>
);

export const FeedbackRefs = ({ ids }) => (
  <span className="ml-1 inline-flex flex-wrap gap-1">{ids.slice(0, 6).map((id, i) => <Link key={id} to={`/app/feedbacks/${id}`} className="rounded bg-slate-100 px-1.5 text-[11px] text-slate-600 hover:bg-slate-200" data-testid="ins-evidence-link">avis {i + 1}</Link>)}</span>
);
